import {
  DEFAULT_TRACKABLE_ITEM_TYPE_ICON,
  getImageUrl,
} from '@/utils/imageUrl';
import i18n from '@/utils/i18n';
import { useHistory } from 'react-router';
import { Routes } from '@/utils/routes';
import { Link } from 'react-router-dom';

export type TrackableItemForDisplay = {
  id: string;
  name: string;
  item_type: {
    name: string;
    category: string;
    color_hex_code?: string | null;
    image_url?: string | null;
  };
};

function getImageSize(size?: 's' | 'm' | 'l' | 'xl' | 'xxl') {
  const sizeConfig = {
    height: 50,
    cssSuffix: '',
  };

  switch (size) {
    case 's':
      sizeConfig.height = 40;
      sizeConfig.cssSuffix = '-small';
      break;
    case 'm':
      sizeConfig.height = 50;
      sizeConfig.cssSuffix = '';
      break;
    case 'l':
      sizeConfig.height = 80;
      sizeConfig.cssSuffix = '-big';
      break;
    case 'xl':
      sizeConfig.height = 110;
      sizeConfig.cssSuffix = '-xl';
      break;
    case 'xxl':
      sizeConfig.height = 256;
      sizeConfig.cssSuffix = '-xxl';
      break;
    default:
      // Default to medium size
      break;
  }

  return sizeConfig;
}

type TrackableItemDisplayProps = {
  trackableItem: TrackableItemForDisplay | undefined;
  showTrackableItemId?: boolean;
  showName?: boolean;
  showLink?: boolean;
  linkInNewTab?: boolean;
  showTypeInsteadOfName?: boolean;
  size?: 's' | 'm' | 'l' | 'xl' | 'xxl';
};

const TrackableItemDisplay = ({
  trackableItem,
  showTrackableItemId = true,
  showName = true,
  showLink = true,
  linkInNewTab = false,
  showTypeInsteadOfName = false,
  size = 'm',
}: TrackableItemDisplayProps) => {
  const history = useHistory();

  if (trackableItem === undefined) {
    return (
      <img
        className="img-thumbnail container-image align-middle"
        src={getImageUrl(50, DEFAULT_TRACKABLE_ITEM_TYPE_ICON)}
        alt={i18n.t('TrackableItem:unknownTrackableItem')}
        title={i18n.t('TrackableItem:unknownTrackableItem')}
      />
    );
  }

  const { name, id, item_type } = trackableItem;
  const { category, color_hex_code } = item_type;

  const RenderNameAndId = () => {
    if (!showName) {
      return null;
    }

    return (
      <>
        {showName && (
          <div
            className={`d-flex ${
              showTrackableItemId
                ? 'flex-column align-middle'
                : 'align-items-center'
            } p-2`}
            title={
              showTypeInsteadOfName
                ? `type: ${item_type.name}`
                : showTrackableItemId
                  ? `id: ${id} | name: ${name}`
                  : `name: ${name}`
            }
          >
            <div>{showTypeInsteadOfName ? item_type.name : name}</div>
            {showTrackableItemId && (
              <div>
                {i18n.t('Common:id')}: {id}
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  const linkUrl =
    category === 'ASSET'
      ? Routes.trackableItem(id)
      : Routes.containerStation(id);

  let sizeConfig = getImageSize(size);

  return (
    <div className="d-flex align-middle">
      <div className="align-middle">
        <div className={`container-image-container${sizeConfig.cssSuffix}`}>
          <img
            className={`img-thumbnail align-middle container-image${sizeConfig.cssSuffix}`}
            src={getImageUrl(
              sizeConfig.height,
              item_type.image_url || DEFAULT_TRACKABLE_ITEM_TYPE_ICON,
            )}
            alt={`Trackable Item ${name}`}
            title={`Trackable Item ${name}`}
            onClick={() => {
              if (showLink) {
                history.push(linkUrl);
              }
            }}
          />
          <div
            className="container-color-overlay"
            style={{ backgroundColor: `#${color_hex_code}` }}
          />
        </div>
      </div>

      {showLink && (
        <Link
          to={linkUrl}
          target={linkInNewTab ? '_blank' : '_self'}
          rel="noreferrer"
        >
          <RenderNameAndId />
        </Link>
      )}
      {!showLink && <RenderNameAndId />}
    </div>
  );
};

export default TrackableItemDisplay;
